import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { toastWarning } from '@components/Toast';

import useClickOutside from '@hooks/useClickOutside';

import { dashboardComparisonColors, repeatColors } from '@features/crm/utils/helpers';
import MyChart from '@features/crm/v3/dashboard/components/MyChart';
import { generateCRMDashboardParams } from '@features/filter/static/filterUtil';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { getCRMDashboardYears } from '@services/crmService';

const overAllBarList = [
	{
		fill: '#0095FF',
		dataKey: 'target',
		title: 'Target'
	},
	{ fill: '#F47920', dataKey: 'pipeline', title: 'Pipeline' },
	{ fill: '#00E096', dataKey: 'achieved', title: 'Achieved' }
];

const PerformanceChart = ({ dataFetcher, title, varName, numberOfBars }) => {
	const filterFields = useSelector((store) => store.filterFieldStore);
	const [isCompareEnable, setIsCompareEnable] = useState(false);
	const [selectedYears, setSelectedYears] = useState([]);
	const [isShowYears, setIsShowYears] = useState(false);
	const [chartData, setChartData] = useState([]);
	const [, setLabels] = useState([]);
	const [years, setYears] = useState([]);
	const [yearWithColor, setYearWithColor] = useState([]);
	const dispatch = useDispatch();
	const yearRef = useRef(null);

	const onOutSideClick = () => {
		if (isShowYears) {
			setIsShowYears(false);
		}
	};

	useClickOutside(yearRef, onOutSideClick);

	const generateParams = (isCompareEnable) => {
		if (selectedYears.length > 0 && isCompareEnable) {
			return {
				yearList: selectedYears
			};
		}
		return {
			...generateCRMDashboardParams(filterFields)
		};
	};

	const generateLabels = (data) => {
		const modifiedLabels = (data) => {
			return data?.map((item) => {
				return item?.[varName]?.name;
			});
		};

		if (!isCompareEnable || selectedYears.length === 0) {
			setLabels(modifiedLabels(data));
		} else {
			let labelList = [];
			for (let item in data) {
				let groupList = data[item];
				labelList.push(groupList[0]?.[varName].name);
			}
			setLabels(labelList);
		}
	};

	const generateNewChartData = (data) => {
		if (!isCompareEnable || selectedYears.length === 0) {
			setYearWithColor([]);
			setChartData([
				...data.map((item) => ({
					name: item?.country?.name
						? item.country.name
						: item?.brand?.name
							? item.brand.name
							: item?.teamMember?.name
								? item.teamMember.name
								: '',
					achieved: item?.achievedAmount || 0,
					pipeline: item?.revenuePipeLineAmount || 0,
					target: item?.targetAmount || 0
				}))
			]);
		} else {
			const years = [
				...new Set(
					Object.values(data)
						.flat()
						.map((item) => item.year)
				)
			].sort((a, b) => a - b);

			const yearsWithColors = years.map((year, index) => ({
				year,
				fill: repeatColors(dashboardComparisonColors, selectedYears.length)[index]
			}));
			setYearWithColor(yearsWithColors);

			const mapDataByPeriod = (data, country, brand, teamMember) => {
				return data.reduce((acc, item) => {
					const { year, achievedAmount } = item;
					const name = item[country]
						? item[country].name
						: item[brand]
							? item[brand].name
							: item?.[teamMember]
								? item?.[teamMember].name
								: '';
					let existingItem = acc.find((entry) => entry.name === name);
					if (!existingItem) {
						existingItem = {
							name: name
						};
						acc.push(existingItem);
					}
					existingItem[year] = achievedAmount;
					return acc;
				}, []);
			};

			const result = mapDataByPeriod(Object.values(data).flat(), 'country', 'brand', 'teamMember');

			setChartData(result);
		}
	};

	useEffect(() => {
		//if (filterFields?.dateRange?.[0]?.key) {
		(async () => {
			try {
				dispatch(openLoader());
				const data =
					selectedYears.length > 0 && isCompareEnable
						? await dataFetcher.compare(generateParams(isCompareEnable))
						: await dataFetcher.get(generateParams(isCompareEnable));
				generateLabels(data);
				generateNewChartData(data);
				dispatch(closeLoader());
			} catch {
				dispatch(closeLoader());
			}
		})();
		//}
	}, [filterFields, selectedYears, isCompareEnable]);

	useEffect(() => {
		//Working
		if (isCompareEnable) {
			(async () => {
				try {
					dispatch(openLoader());
					let params = generateCRMDashboardParams(filterFields, false);
					delete params.dateRange;
					const data = await getCRMDashboardYears(params);
					setYears(data);
					dispatch(closeLoader());
				} catch {
					dispatch(closeLoader());
				}
			})();
		} else {
			if (!isShowYears) setSelectedYears([]);
		}
	}, [isCompareEnable, filterFields]);

	const renderChartTitles = () => {
		if (yearWithColor?.length === 0) {
			return (
				<div className="d-flex flex-wrap gap-6  mr-5">
					<div className="target d-flex">
						<span className="color-indicator target" />
						<p className="regular-12 weight-500">Target</p>
					</div>
					<div className="pipeline d-flex">
						<span className="color-indicator pipeline" />
						<p className="regular-12 weight-500">Pipeline</p>
					</div>
					<div className="pipeline d-flex">
						<span className="color-indicator achieved" />
						<p className="regular-12 weight-500">Achieved</p>
					</div>
				</div>
			);
		} else {
			return (
				<div className="d-flex gap-6 mr-5">
					{yearWithColor?.map((item) => (
						<div className="target d-flex" key={`year_${item.year}`}>
							<span
								className="color-indicator"
								style={{
									background: item.fill
								}}
							/>
							<p className="regular-12 weight-500">{item.year}</p>
						</div>
					))}
				</div>
			);
		}
	};
	return (
		<>
			<div className="performance-heading d-flex justify-content-between align-items-center">
				<p className="regular-14 gray_dark_01 weight-500">{title}</p>
				<div className="dot-content-toggle d-flex align-items-center">
					{renderChartTitles()}
					<div className="quarterly-and-compaire d-flex">
						<div
							className="d-flex align-items-center cursor-pointer"
							onClick={() => setIsCompareEnable((prev) => !prev)}
						>
							<p className="regular-14 gray_dark_01 mr-2">Compare</p>
							<img
								src={
									isCompareEnable
										? `${process.env.PUBLIC_URL}/icons/toggle-on.svg`
										: `${process.env.PUBLIC_URL}/icons/toggle-off.svg`
								}
								alt="arrow"
							/>
						</div>
						{isCompareEnable && (
							<div className="select-component order-column-filter">
								<div
									className="click-box click mb-1"
									onClick={() => setIsShowYears((prev) => !prev)}
								>
									<p className="regular-14 ">
										{yearWithColor.length > 0 ? `${yearWithColor.length} Years` : 'Years'}
									</p>
									<img src={`${process.env.PUBLIC_URL}/images/chevron_down.svg`} alt="" />
								</div>
								<div ref={yearRef}>
									{isShowYears && (
										<div className="dropdown-box">
											{years?.map((item) => (
												<div className="custom-chekbox" key={`year_${item}`}>
													<div className="form-group">
														<input
															type="checkbox"
															id={`brand_year_${item}`}
															onChange={() => {
																if (selectedYears.includes(item)) {
																	setSelectedYears((prev) =>
																		prev.filter((year) => year !== item)
																	);
																} else {
																	if (selectedYears.length < 5) {
																		setSelectedYears((prev) => {
																			const updatedYears = [
																				...prev,
																				item
																			];
																			return updatedYears.sort(
																				(a, b) => a - b
																			); // Sort in ascending order
																		});
																	} else {
																		toastWarning(
																			'Maximum 5 years selectable at a time!'
																		);
																	}
																}
															}}
															checked={selectedYears.includes(item)}
														/>
														<label htmlFor={`brand_year_${item}`}>
															<span>{item}</span>
														</label>
													</div>
												</div>
											))}
										</div>
									)}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
			<div className="performance-body">
				<MyChart
					yearWithColor={yearWithColor}
					chartData={chartData}
					maxBarsVisible={16}
					barWidth={100}
					isCompareEnable={isCompareEnable}
					barList={overAllBarList}
					numberOfBars={numberOfBars}
				/>
			</div>
		</>
	);
};

export default PerformanceChart;
